/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { redirect } from '@tanstack/react-router'
import { userHasAcceptedRequiredLegalPolicies } from '@web/src/lib/legal/policy-version'
import type { SessionUser } from '@web/src/lib/yoga/yoga-auth-context'

/** Paths reachable while a user still needs to re-accept legal policies. */
const LEGAL_POLICY_GATE_ALLOWLIST = [
  '/auth/accept-policies',
  '/auth/policies-declined',
  '/helpcenter',
  '/account',
  '/unsubscribe',
] as const

export function isLegalPolicyGateAllowlistedPath(pathname: string): boolean {
  return LEGAL_POLICY_GATE_ALLOWLIST.some((allowed) => pathname === allowed || pathname.startsWith(`${allowed}/`))
}

/**
 * Redirect logged-in users who have not accepted the required policy floor
 * to the re-acceptance page (unless they are already on an allowlisted path).
 */
export function redirectIfLegalPoliciesNeedAcceptance(currentUser: SessionUser, pathname: string): void {
  if (!currentUser) {
    return
  }

  if (userHasAcceptedRequiredLegalPolicies(currentUser)) {
    return
  }

  if (isLegalPolicyGateAllowlistedPath(pathname)) {
    return
  }

  throw redirect({
    href: `/auth/accept-policies?redirectTo=${encodeURIComponent(pathname)}`,
  })
}
